/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipEnhancementInfo, PlaylistClip } from "types/Clip";
import { BlockTypes } from "constants/BlockTypes";
import { AudioEffect } from "types/AudioEffect";
import { Asset } from "types/Asset";
import { EnhancedLogger } from "utilities/Logger";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export class EnhanceV1MixEffect extends AudioEffect {
  private readonly mixNode: GainNode;
  private readonly originalGain: GainNode;
  private readonly enhancedGain: GainNode;
  private readonly splitter: ChannelSplitterNode;

  static override providePermanentUrl(
    clip: PlaylistClip,
    asset: Asset
  ): string | undefined {
    if (!EnhanceV1MixEffect.canBuildFor(clip) || !asset.streamingAudioV1Url) {
      return undefined;
    }

    return asset.streamingAudioV1Url;
  }

  static override canUseLocalAsset(): boolean {
    return false;
  }

  static override canBuildFor(clip: PlaylistClip): clip is PlaylistClip & {
    type: BlockTypes.TRANSCRIPTION;
    enhancementInfo: ClipEnhancementInfo;
  } {
    return (
      clip?.type === BlockTypes.TRANSCRIPTION &&
      clip?.enhancementInfo !== undefined &&
      clip?.enhancementInfo?.enhanceSpeechModelVersion === ModelVersion.v1
    );
  }

  constructor(
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number,
    logger?: EnhancedLogger
  ) {
    if (!EnhanceV1MixEffect.canBuildFor(clip)) {
      throw Error(
        "Can't apply enhance v1 mix node to a clip without enhancementInfo"
      );
    }

    super(audioContext, clip, playlistTime, logger);

    this.enhancedGain = this.audioContext.createGain();
    this.originalGain = this.audioContext.createGain();
    this.mixNode = this.audioContext.createGain();
    this.splitter = this.audioContext.createChannelSplitter(2);

    this.splitter.connect(this.originalGain, 0);
    this.splitter.connect(this.enhancedGain, 1);
    this.originalGain.connect(this.mixNode);
    this.enhancedGain.connect(this.mixNode);

    this.hotswap(clip);
  }

  hotswap(newClip: PlaylistClip): void {
    if (newClip.enhancementInfo === undefined) {
      this.logger.debug(
        `❄️ NO HOTSWAP!!! No enhancement info in new clip:`,
        newClip
      );
      return;
    }

    this.logger.debug(
      `🔥 Hotswap BEFORE - original audio gain: ${this.originalGain.gain.value}, enhanced audio gain : ${this.enhancedGain.gain.value}`
    );

    this.originalGain.gain.value =
      1 - newClip.enhancementInfo.strengthPercent / 100;

    this.enhancedGain.gain.value =
      newClip.enhancementInfo.strengthPercent / 100;

    this.logger.debug(
      `🔥 Hotswap AFTER applying ${newClip.enhancementInfo.strengthPercent} strength - original audio gain: ${this.originalGain.gain.value}, enhanced audio gain : ${this.enhancedGain.gain.value}`
    );
  }

  get inputNode(): AudioNode {
    return this.splitter;
  }

  get outputNode(): AudioNode {
    return this.mixNode;
  }

  disconnect(): void {
    this.splitter.disconnect();
    this.enhancedGain.disconnect();
    this.originalGain.disconnect();
    this.mixNode.disconnect();
  }
}
